// Chip runtime state for the gameweek grid (extracted from
// GameweekGrid.tsx). Owns the per-chip `ChipRuntimeConfig` map plus the four label
// helpers ChipRow/ChipInfoModal read, so the grid only wires them up.
import { CHIP_ICONS } from '../chipIcons'
import { chipDefinitions } from '../../mockChips'
import { CHIP_SEASON_CAPS, SECOND_UNLOCK_GW } from '../../chipRules'
import { getChipStatus, type ChipRuntimeConfig, type ChipUsedDetail } from '../../chipStatus'
import type { ChipId, ChipPlayConfirmation } from '../../types'

export const CHIP_MULTIPLIER_DESCRIPTIONS: Partial<Record<ChipId, string>> = {
  doubleUp: 'doubled',
  goalfest: 'boosted by +1 per goal in a fixture you correctly call the outcome for',
  // Not a multiplier on the normal 5/3/1 scoring — an all-or-nothing replacement scheme.
  cleanSweep:
    'all-or-nothing: 10p per game (20p if exact) if you clear every outcome, or 0 for the whole gameweek if you miss one',
}

// Chips whose play asks a second time. Clean Sweep is the only one where a
// mis-tap costs anything: one wrong outcome zeroes the whole gameweek. A chip with no entry
// here keeps the one-tap play, so a future high-risk chip is one object away.
//
// The copy is a risk affirmation, not a second explanation: the chip's own description sits
// right above it in the modal and already spells out the 10/20/zero scoring, so repeating it
// here is noise. It also avoids calling the play permanent — it stays reversible until the
// deadline.
export const CHIP_PLAY_CONFIRMATIONS: Partial<Record<ChipId, ChipPlayConfirmation>> = {
  cleanSweep: {
    title: 'Are you sure?',
    body: 'Clean Sweep is all or nothing, and you get two a season.',
    confirmLabel: "I'm sure",
  },
}

export function useGameweekChips({
  gameweek,
  deadlinePassed,
  doubleUpUsedGameweeks,
  cleanSweepUsedGameweeks,
  goalfestUsedGameweeks,
  goalfestFinalBonusByGameweek,
  cleanSweepFinalTotalByGameweek,
}: {
  gameweek: number
  deadlinePassed: boolean
  doubleUpUsedGameweeks: number[]
  cleanSweepUsedGameweeks: number[]
  goalfestUsedGameweeks: number[]
  goalfestFinalBonusByGameweek: Record<number, number>
  cleanSweepFinalTotalByGameweek: Record<number, number>
}) {
  const isDoubleUpActive = doubleUpUsedGameweeks.includes(gameweek)
  const isCleanSweepActive = cleanSweepUsedGameweeks.includes(gameweek)
  const isGoalfestActive = goalfestUsedGameweeks.includes(gameweek)

  const chipConfigs: Partial<Record<ChipId, ChipRuntimeConfig>> = {
    doubleUp: {
      usedGameweeks: doubleUpUsedGameweeks,
      seasonCap: CHIP_SEASON_CAPS.doubleUp,
      isEligible: true,
      hasCooldown: false,
      secondUseUnlockGameweek: SECOND_UNLOCK_GW,
      deadlinePassed,
    },
    cleanSweep: {
      usedGameweeks: cleanSweepUsedGameweeks,
      seasonCap: CHIP_SEASON_CAPS.cleanSweep,
      isEligible: true,
      hasCooldown: false,
      secondUseUnlockGameweek: SECOND_UNLOCK_GW,
      deadlinePassed,
    },
    goalfest: {
      usedGameweeks: goalfestUsedGameweeks,
      seasonCap: CHIP_SEASON_CAPS.goalfest,
      isEligible: true,
      hasCooldown: false,
      secondUseUnlockGameweek: SECOND_UNLOCK_GW,
      deadlinePassed,
    },
  }

  const getStatus = (chipId: ChipId) => {
    const chip = chipDefinitions.find((c) => c.id === chipId)!
    return getChipStatus(chipId, chip.implemented, gameweek, chipConfigs)
  }

  const getStatusDetail = (chipId: ChipId): string | undefined => {
    const config = chipConfigs[chipId]
    if (config?.unlockGameweek !== undefined && gameweek < config.unlockGameweek) {
      return `GW${config.unlockGameweek}`
    }
    return undefined
  }

  // The played gameweek for every used chip, plus Goalfest/Clean Sweep's frozen-record suffix
  // once it's known. ChipRow composes the "Played GW N · …"
  // string; this just supplies the two parts.
  //
  // The latest use, not the first: with two charges a season, leading with the first play would
  // hide the second one's gameweek and its record behind a number from months earlier.
  const getUsedDetail = (chipId: ChipId): ChipUsedDetail | undefined => {
    const usedGameweeks = chipConfigs[chipId]?.usedGameweeks ?? []
    if (usedGameweeks.length === 0) return undefined
    const usedGameweek = Math.max(...usedGameweeks)

    if (chipId === 'goalfest') {
      const finalBonus = goalfestFinalBonusByGameweek[usedGameweek]
      return { gameweek: usedGameweek, suffix: finalBonus === undefined ? undefined : `+${finalBonus}` }
    }
    if (chipId === 'cleanSweep') {
      const finalTotal = cleanSweepFinalTotalByGameweek[usedGameweek]
      // Clean Sweep's number is a full point total (including 0 for a missed sweep), not a
      // bonus on top of something else — no leading '+' unlike Goalfest's.
      return { gameweek: usedGameweek, suffix: finalTotal === undefined ? undefined : `${finalTotal}p` }
    }
    return { gameweek: usedGameweek }
  }

  const getBlockingChipName = (chipId: ChipId): string | undefined => {
    const blockerId = (Object.keys(chipConfigs) as ChipId[]).find(
      (otherId) =>
        otherId !== chipId &&
        chipConfigs[otherId]?.usedGameweeks.includes(gameweek)
    )
    return blockerId
      ? chipDefinitions.find((c) => c.id === blockerId)?.name
      : undefined
  }

  // Active chip for this gameweek — takes priority over the
  // tier band on the GW Points card, since a played chip is a more specific,
  // higher-signal fact about the gameweek than its points-derived tier.
  const activeChip = chipDefinitions.find(
    (chip) => getChipStatus(chip.id, chip.implemented, gameweek, chipConfigs) === 'active'
  )
  const activeChipIcon = activeChip ? CHIP_ICONS[activeChip.id] : null

  return {
    isDoubleUpActive,
    isCleanSweepActive,
    isGoalfestActive,
    chipConfigs,
    getStatus,
    getStatusDetail,
    getUsedDetail,
    getBlockingChipName,
    activeChip,
    activeChipIcon,
  }
}

export type GameweekChips = ReturnType<typeof useGameweekChips>
